import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, API } from "../../App";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  Laptop,
  Wrench,
  FileText,
  Package,
  ShoppingCart,
  Store,
  BarChart3,
  Settings,
  Menu,
  LogOut,
  User,
  ChevronDown,
  Cpu,
  Bell,
  Coins,
  BookOpen,
  Wallet
} from "lucide-react";

const allNavigation = [
  { name: "Tableau de bord", href: "/dashboard", icon: LayoutDashboard, roles: ["admin", "vendeur", "technicien"] },
  { name: "Clients", href: "/clients", icon: Users, roles: ["admin", "vendeur"] },
  { name: "Appareils", href: "/devices", icon: Laptop, roles: ["admin", "vendeur"] },
  { name: "Dossiers SAV", href: "/sav", icon: Wrench, roles: ["admin", "vendeur", "technicien"] },
  { name: "Devis", href: "/quotes", icon: FileText, roles: ["admin", "vendeur", "technicien"] },
  { name: "Stock Pièces", href: "/parts", icon: Cpu, roles: ["admin", "vendeur", "technicien"] },
  { name: "Produits", href: "/products", icon: Package, roles: ["admin", "vendeur"] },
  { name: "Ventes", href: "/sales", icon: ShoppingCart, roles: ["admin", "vendeur"] },
  { name: "Caisse", href: "/pos", icon: Store, roles: ["admin", "vendeur"] },
  { name: "Livres", href: "/book-proposals", icon: BookOpen, roles: ["admin", "vendeur"] },
  { name: "Crédits", href: "/credits", icon: Wallet, roles: ["admin", "vendeur"] },
  { name: "Commissions", href: "/commissions", icon: Coins, roles: ["admin"] },
  { name: "Statistiques", href: "/statistics", icon: BarChart3, roles: ["admin", "vendeur"] },
];

const adminNavigation = [
  { name: "Utilisateurs", href: "/users", icon: Users },
  { name: "Paramètres", href: "/settings", icon: Settings },
];

const NavItem = ({ item, mobile, onClick }) => (
  <NavLink
    to={item.href}
    onClick={onClick}
    className={({ isActive }) =>
      cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
        isActive
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
      )
    }
  >
    <item.icon className="h-5 w-5 flex-shrink-0" />
    <span>{item.name}</span>
  </NavLink>
);

const Sidebar = ({ mobile, onClose }) => {
  const { user } = useAuth();
  
  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-28 items-center justify-center px-2 border-b border-border">
        <img src="/logo.png" alt="Micro Multimédia" className="h-24 w-auto object-contain" />
      </div>
      
      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {allNavigation
            .filter((item) => item.roles.includes(user?.role))
            .map((item) => (
              <NavItem key={item.href} item={item} mobile={mobile} onClick={onClose} />
            ))}
          
          {user?.role === "admin" && (
            <>
              <div className="my-4 border-t border-border" />
              <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Administration
              </p>
              {adminNavigation.map((item) => (
                <NavItem key={item.href} item={item} mobile={mobile} onClick={onClose} />
              ))}
            </>
          )}
        </nav>
      </ScrollArea>
      
      {/* User info */}
      <div className="border-t border-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
            {user?.prenom?.[0]}{user?.nom?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.prenom} {user?.nom}</p>
            <p className="text-xs text-muted-foreground capitalize">
              {user?.role === "admin" ? "Administrateur" : user?.role === "vendeur" ? "Vendeur" : user?.role === "technicien" ? "Technicien" : user?.role}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0, pending_quotes: 0, urgent_cases: 0 });
  const [notifOpen, setNotifOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await API.get("/notifications");
      setNotifications(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r border-border bg-card lg:block">
        <Sidebar />
      </aside>
      
      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <Sidebar mobile onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      
      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 px-4 md:px-6">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="flex-1" />
          
          {/* Notifications */}
          <Popover open={notifOpen} onOpenChange={setNotifOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="h-5 w-5" />
                {notifications.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                    {notifications.unread_count}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-popover">
              <div className="px-4 py-3 border-b">
                <p className="font-semibold text-sm">Notifications</p>
              </div>
              {(notifications.pending_quotes > 0 || notifications.urgent_cases > 0) && (
                <div className="px-4 py-2 border-b bg-secondary/30 space-y-1">
                  {notifications.pending_quotes > 0 && (
                    <p
                      className="text-xs text-amber-600 font-medium cursor-pointer hover:underline"
                      onClick={() => { navigate("/quotes"); setNotifOpen(false); }}
                    >
                      {notifications.pending_quotes} devis en attente de réponse
                    </p>
                  )}
                  {notifications.urgent_cases > 0 && (
                    <p
                      className="text-xs text-red-600 font-medium cursor-pointer hover:underline"
                      onClick={() => { navigate("/sav"); setNotifOpen(false); }}
                    >
                      {notifications.urgent_cases} dossier(s) urgent(s)
                    </p>
                  )}
                </div>
              )}
              <ScrollArea className="max-h-64">
                {notifications.notifications.length > 0 ? (
                  <div className="divide-y">
                    {notifications.notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className="px-4 py-3 hover:bg-secondary/30 cursor-pointer transition-colors"
                        onClick={() => {
                          if (notif.sav_case_id) navigate(`/sav/${notif.sav_case_id}`);
                          setNotifOpen(false);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <Wrench className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{notif.sav_case_numero}</p>
                            <p className="text-xs text-muted-foreground truncate">{notif.message || notif.statut}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {new Date(notif.timestamp).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Aucune notification</p>
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
          
          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
                  {user?.prenom?.[0]}{user?.nom?.[0]}
                </div>
                <span className="hidden md:block">{user?.prenom} {user?.nom}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <User className="mr-2 h-4 w-4" />
                Profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="mr-2 h-4 w-4" />
                Paramètres
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        
        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
